import React from 'react';
import {
  RiFileTextLine,
  RiScan2Line,
  RiFileListLine,
  RiCheckLine,
  RiExternalLinkLine,
} from '@remixicon/react';

export type WorkflowStatus = 'running' | 'stopped' | 'editing' | 'published';

export type NodeType = {
  type: string;
  label: string;
  icon: React.ReactNode;
};

export type NodeConstraint = {
  maxCount: number;
  canConnectFrom: string[];
  canConnectTo: string[];
};

export type NodeConstraints = {
  [key: string]: NodeConstraint;
};

export const WorkflowComponentTypes = {
  DocInputComponent: 'document_input',
  DigitizeComponent: 'digitize',
  DocTypeComponent: 'document_type',
  DocReviewComponent: 'document_review',
  DocExportComponent: 'document_export',
};

export const ComponentNames = {
  'document_input': 'Document Input',
  'digitize': 'Digitize',
  'document_type': 'Document Type',
  'document_review': 'Document Review',
  'document_export': 'Document Export'
};

export const availableNodeTypesConfig = [
  { type: 'document_input', labelKey: 'WORKFLOW_NODE_DOCUMENT_INPUT', icon: <RiFileTextLine size={20} color="#3E8AFF" /> },
  { type: 'digitize', labelKey: 'WORKFLOW_NODE_DIGITIZE', icon: <RiScan2Line size={20} color="#3E8AFF" /> },
  { type: 'document_type', labelKey: 'WORKFLOW_NODE_DOCUMENT_TYPE', icon: <RiFileListLine size={20} color="#3E8AFF" /> },
  { type: 'document_review', labelKey: 'WORKFLOW_NODE_DOCUMENT_REVIEW', icon: <RiCheckLine size={20} color="#3E8AFF" /> },
  { type: 'document_export', labelKey: 'WORKFLOW_NODE_EXPORT', icon: <RiExternalLinkLine size={20} color="#3E8AFF" /> },
];

export const nodeConstraints: NodeConstraints = {
  'document_input': {
    maxCount: 1,
    canConnectFrom: [],
    canConnectTo: ['digitize', 'document_type', 'document_review', 'document_export']
  },
  'digitize': {
    maxCount: 1,
    canConnectFrom: ['document_input'],
    canConnectTo: ['document_type', 'document_review', 'document_export']
  },
  'document_type': {
    maxCount: 1,
    canConnectFrom: ['document_input', 'digitize'],
    canConnectTo: ['document_review', 'document_export']
  },
  'document_review': {
    maxCount: 1,
    canConnectFrom: ['document_input', 'digitize', 'document_type'],
    canConnectTo: ['document_export']
  },
  'document_export': {
    maxCount: -1,
    canConnectFrom: ['document_input', 'digitize', 'document_type', 'document_review'],
    canConnectTo: ['document_review']
  }
};

export const getNodeIcon = (nodeType: string) => {
  switch (nodeType) {
    case 'document_input':
      return <RiFileTextLine size={24} color="#0066FF" />;
    case 'digitize':
      return <RiScan2Line size={24} color="#0066FF" />;
    case 'document_type':
      return <RiFileListLine size={24} color="#0066FF" />;
    case 'document_review':
      return <RiCheckLine size={24} color="#0066FF" />;
    case 'document_export':
      return <RiExternalLinkLine size={24} color="#0066FF" />;
    default:
      return <RiFileTextLine size={24} color="#0066FF" />;
  }
};

export interface ValidationError {
  nodeId: string;
  nodeType: string;
  nodeLabel?: string;
  message: string;
  field?: string;
  context?: any;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export const MandatoryComponents = [
  WorkflowComponentTypes.DocInputComponent,
  WorkflowComponentTypes.DigitizeComponent,
  WorkflowComponentTypes.DocTypeComponent,
  WorkflowComponentTypes.DocReviewComponent,
  WorkflowComponentTypes.DocExportComponent,
];

export const NodeValidators = {
  document_input: (nodeId: string, nodeLabel: string, configuration: Record<string, any>): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!configuration.fileFormats || !Array.isArray(configuration.fileFormats) || configuration.fileFormats.length === 0) {
      errors.push({
        nodeId,
        nodeType: 'document_input',
        nodeLabel,
        message: 'WORKFLOW_VALIDATION_INPUT_FORMATS_REQUIRED',
        field: 'fileFormats'
      });
    }

    if (!configuration.source || !Array.isArray(configuration.source) || configuration.source.length === 0) {
      errors.push({
        nodeId,
        nodeType: 'document_input',
        nodeLabel,
        message: 'WORKFLOW_VALIDATION_INPUT_SOURCES_REQUIRED',
        field: 'source'
      });
    }

    return errors;
  },

  document_type: (nodeId: string, nodeLabel: string, configuration: Record<string, any>): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!configuration.selectedDocumentType || configuration.selectedDocumentType.trim() === '') {
      errors.push({
        nodeId,
        nodeType: 'document_type',
        nodeLabel,
        message: 'WORKFLOW_VALIDATION_DOCUMENT_TYPES_REQUIRED',
        field: 'selectedDocumentType'
      });
    }

    return errors;
  },

  document_review: (nodeId: string, nodeLabel: string, configuration: Record<string, any>): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!configuration.users || !Array.isArray(configuration.users)) {
      errors.push({
        nodeId,
        nodeType: 'document_review',
        nodeLabel,
        message: 'WORKFLOW_VALIDATION_REVIEW_USERS_REQUIRED',
        field: 'users'
      });
      return errors;
    }

    const reviewingUsers = configuration.users.filter((user: any) => user.willReview === true);
    if (reviewingUsers.length === 0) {
      errors.push({
        nodeId,
        nodeType: 'document_review',
        nodeLabel,
        message: 'WORKFLOW_VALIDATION_REVIEW_NO_REVIEWERS',
        field: 'reviewingUsers'
      });
    }

    const totalLoadPercentage = reviewingUsers.reduce((sum: number, user: any) => sum + (user.loadPercentage || 0), 0);
    if (reviewingUsers.length > 0 && totalLoadPercentage !== 100) {
      errors.push({
        nodeId,
        nodeType: 'document_review',
        nodeLabel,
        message: 'WORKFLOW_VALIDATION_REVIEW_LOAD_PERCENTAGE',
        field: 'loadPercentage',
        context: { totalLoadPercentage }
      });
    }

    return errors;
  },

  document_export: (nodeId: string, nodeLabel: string, configuration: Record<string, any>): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!configuration.selectedExportFormat) {
      errors.push({
        nodeId,
        nodeType: 'document_export',
        nodeLabel,
        message: 'WORKFLOW_VALIDATION_EXPORT_FORMAT_REQUIRED',
        field: 'selectedExportFormat'
      });
      return errors;
    }

    if (!configuration.selectedExportDestination) {
      errors.push({
        nodeId,
        nodeType: 'document_export',
        nodeLabel,
        message: 'WORKFLOW_VALIDATION_EXPORT_METHOD_REQUIRED',
        field: 'selectedExportDestination'
      });
      return errors;
    }

    return errors;
  },

  digitize: (_nodeId: string, _nodeLabel: string, _configuration: Record<string, any>): ValidationError[] => {
    return [];
  }
};

export const validateWorkflowForPublishing = (nodes: any[]): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!nodes || nodes.length === 0) {
    return {
      isValid: false,
      errors: [{
        nodeId: 'workflow',
        nodeType: 'workflow',
        message: 'WORKFLOW_VALIDATION_EMPTY_WORKFLOW'
      }]
    };
  }

  const presentNodeTypes = nodes.map(node => node.data?.nodeType).filter(Boolean);
  const missingMandatoryComponents = MandatoryComponents.filter(
    mandatoryType => !presentNodeTypes.includes(mandatoryType)
  );

  if (missingMandatoryComponents.length > 0) {
    missingMandatoryComponents.forEach(missingType => {
      const componentName = ComponentNames[missingType as keyof typeof ComponentNames] || missingType;

      errors.push({
        nodeId: 'workflow',
        nodeType: missingType,
        nodeLabel: componentName,
        message: 'WORKFLOW_VALIDATION_MANDATORY_COMPONENT_MISSING',
      });
    });
  }

  if (missingMandatoryComponents.length === 0) {
    nodes.forEach((node) => {
      const { id, data } = node;
      const nodeType = data?.nodeType;
      const nodeLabel = data?.label || `${nodeType} node`;
      const configuration = data?.settings || {};

      const validator = NodeValidators[nodeType as keyof typeof NodeValidators];

      if (validator) {
        const nodeErrors = validator(id, nodeLabel, configuration);
        errors.push(...nodeErrors);
      } else if (nodeType) {
        errors.push({
          nodeId: id,
          nodeType: nodeType,
          nodeLabel,
          message: 'WORKFLOW_VALIDATION_UNKNOWN_NODE_TYPE'
        });
      }
    });
  }

  const componentOrder = Object.values(WorkflowComponentTypes);
  const sortedErrors = errors.sort((a, b) => {
    const aIndex = componentOrder.indexOf(a.nodeType);
    const bIndex = componentOrder.indexOf(b.nodeType);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    if (aIndex !== -1 && bIndex === -1) {
      return -1;
    }

    if (aIndex === -1 && bIndex !== -1) {
      return 1;
    }

    return 0;
  });

  return {
    isValid: sortedErrors.length === 0,
    errors: sortedErrors
  };
};

export const formatValidationErrors = (errors: ValidationError[]): string => {
  return errors.map(error => {
    const nodeInfo = error.nodeLabel ? `"${error.nodeLabel}"` : `Node ${error.nodeId}`;
    return `${nodeInfo}: ${error.message}`;
  }).join('\n');
};

export const groupErrorsByNode = (errors: ValidationError[]): Record<string, ValidationError[]> => {
  return errors.reduce((groups, error) => {
    if (!groups[error.nodeId]) {
      groups[error.nodeId] = [];
    }
    groups[error.nodeId].push(error);
    return groups;
  }, {} as Record<string, ValidationError[]>);
};
